/**
 * ActivityFeed — chronological log of all project changes.
 * Shows field updates, document uploads, license events, and alerts.
 */
import { useState, useEffect } from "react";
import { Edit3, Upload, Send, AlertTriangle, Clock, FileText, Loader2 } from "lucide-react";
import { api } from "../../lib/api.ts";
import { status } from "../../lib/semantics.ts";
import { ErrorMessage } from "./ErrorMessage.tsx";

interface ActivityFeedProps {
  projectId: string;
}

interface Activity {
  type: string;
  target: string;
  detail: string | null;
  extra: string | null;
  actor: string | null;
  timestamp: string;
}

// Icons stay local (lucide components); every COLOUR derives from the one
// status vocabulary — this map once hand-picked amber for routine licence
// events and flat red for every alert severity.
const TYPE_ICONS: Record<string, any> = {
  field_update: Edit3,
  document_upload: Upload,
  license_event: Send,
  alert: AlertTriangle,
};

const MACHINE_ACTORS = new Set(["extraction_agent", "cloud_sync", "aggregation", "system"]);

/** Colour + label from the derivation; machine field-writes read as AI work,
 *  alert rows carry their own severity vocabulary. */
function activityStyle(a: Activity) {
  if (a.type === "alert" && a.extra) return status("alert", a.extra);
  if (a.type === "field_update" && a.actor && MACHINE_ACTORS.has(a.actor)) {
    return status("activity", "field_update_machine");
  }
  return status("activity", a.type);
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function describeActivity(activity: Activity): string {
  switch (activity.type) {
    case "field_update": {
      const value = activity.detail ? `"${activity.detail.slice(0, 40)}${activity.detail.length > 40 ? "..." : ""}"` : "(cleared)";
      // Provenance builds trust: never credit a human with the platform's
      // own writes, and never guess for an unknown actor.
      const ACTOR_LABELS: Record<string, string> = {
        cloud_sync: "Cloud sync",
        extraction_agent: "AI extraction",
        aggregation: "Calculated",
        system: "Enereg",
        user: "User",
      };
      const actor = ACTOR_LABELS[activity.actor ?? "user"] ?? activity.actor;
      return `${actor} set ${activity.target} to ${value}`;
    }
    case "document_upload": {
      const docType = activity.target?.replace(/_/g, " ") || "document";
      const status = activity.extra === "completed" ? " (extracted)" : "";
      return `${activity.detail || docType} uploaded${status}`;
    }
    case "license_event": {
      const eventType = activity.detail?.replace(/_/g, " ") || "event";
      return `${eventType}: ${activity.extra || activity.target}`;
    }
    case "alert": {
      return activity.detail || "Regulatory alert";
    }
    default:
      return `${activity.type}: ${activity.target}`;
  }
}

export function ActivityFeed({ projectId }: ActivityFeedProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    // Stale-response guard: a fast project switch must not show the previous
    // project's history under the new project's name.
    let alive = true;
    setLoading(true);
    setError(false);
    api.getProjectActivity(projectId)
      .then((a) => { if (alive) setActivities(a); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, reloadKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={16} className="text-text-tertiary animate-spin" />
      </div>
    );
  }

  // A failed load is not an empty history — say so, offer retry.
  if (error) {
    return <ErrorMessage message="Couldn't load activity." onRetry={() => setReloadKey(k => k + 1)} />;
  }

  const filtered = filter === "all" ? activities : activities.filter(a => a.type === filter);

  // Group by date
  const groups = new Map<string, Activity[]>();
  for (const a of filtered) {
    const date = a.timestamp ? new Date(a.timestamp).toLocaleDateString() : "Unknown";
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date)!.push(a);
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        {[
          { key: "all", label: "All" },
          { key: "field_update", label: "Fields" },
          { key: "document_upload", label: "Documents" },
          { key: "license_event", label: "Licenses" },
          { key: "alert", label: "Alerts" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 rounded-full text-[13px] font-medium transition-colors whitespace-nowrap ${
              filter === f.key ? "bg-brand text-on-solid" : "bg-surface-tertiary text-text-secondary hover:bg-border"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-[13px] text-text-tertiary text-center py-4">No activity recorded yet.</p>
      ) : (
        <div className="space-y-4">
          {Array.from(groups.entries()).map(([date, items]) => (
            <div key={date}>
              <div className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-2">{date}</div>
              <div className="space-y-1">
                {items.map((activity, i) => {
                  const style = activityStyle(activity);
                  const Icon = TYPE_ICONS[activity.type] ?? Clock;

                  return (
                    <div key={`${activity.type}-${activity.target}-${i}`} className="flex items-start gap-2.5 px-2 py-1.5 rounded-lg hover:bg-surface-secondary transition-colors">
                      <Icon size={14} className={`${style.text} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-text-primary">{describeActivity(activity)}</div>
                      </div>
                      <span className="text-[11px] text-text-tertiary flex-shrink-0">{formatTimestamp(activity.timestamp)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
